import { getLocaleFirstDayOfWeek } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injectable,
  input,
  Input,
  LOCALE_ID,
  OnChanges,
  output,
  Output,
  QueryList,
  Signal,
  SimpleChanges,
  viewChild,
  ViewChildren,
} from '@angular/core';
import { type CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { refreshOnDisplayZoneChange } from '@modules/full-calendar/types/display-zone-refresh';
import { DragData } from '@modules/drag-n-drop/types/drag-data';
import { AngularCalendarHelper } from '@modules/full-calendar/types/AngularCalendarHelper';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { type CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { type CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import { beginningOfDay, endOfDay } from '@sharedlib/common/utils/date';
import { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import {
  type CalendarEvent as AngularCalendarEvent,
  CalendarDateFormatter,
  CalendarMonthViewComponent,
  CalendarNativeDateFormatter,
  type DateFormatterParams,
} from 'angular-calendar';
import { CalendarMonthDayCellComponent } from '../calendar-month-day-cell/calendar-month-day-cell.component';

@Injectable()
class CustomDateFormatter extends CalendarNativeDateFormatter {
  private readonly dateFormatService = inject(DateFormatService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  // SBUX-829: the weekday-name header is date-only (browser-local); render the column date's
  // DISPLAY-zone wall clock so the header row is not rotated relative to the account-zone day cells.
  public monthViewColumnHeader({ date }: DateFormatterParams): string {
    return this.dateFormatService.formatDayOfWeekShort(
      toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone()),
    );
  }
}

@Component({
  selector: 'stch-calendar-month',
  templateUrl: './calendar-month.component.html',
  styleUrls: ['./calendar-month.component.less'],
  providers: [{ provide: CalendarDateFormatter, useClass: CustomDateFormatter }],
  standalone: false,
})
export class CalendarMonthComponent implements OnChanges {
  protected readonly LOCALE_ID = inject(LOCALE_ID);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /** Re-lays the grid out when the display zone arrives; bound to the view's [refresh]. */
  protected readonly displayZoneRefresh = refreshOnDisplayZoneChange();

  /**
   * The day to mark, already projected into the display frame (see full-calendar). Compared with
   * `sameDisplayDay`, which re-anchors each cell's account-midnight instant into the same frame —
   * a browser-local `sameDay` would ring the neighbouring cell when account zone != device zone.
   */
  @Input() selectedDate: Date | null;
  @Input() viewDate: Date;
  @Input() events: AngularCalendarEvent<CalendarEventSummaryDto>[];
  @Input() eventsAppearance: 'list' | 'dots' = 'dots';
  public readonly scrollToDate = input<Date>();

  @Output() dateClicked: EventEmitter<CalendarCellClickEvent> = new EventEmitter<CalendarCellClickEvent>();
  @Output() dateDblClicked: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() eventDropped: EventEmitter<CalendarDropEvent> = new EventEmitter<CalendarDropEvent>();

  protected readonly eventClicked = output<CalendarEvent>();
  protected readonly eventDblClicked = output<CalendarEvent>();

  protected readonly firstDayOfWeek = computed<number>(() => getLocaleFirstDayOfWeek(this.LOCALE_ID));

  public displayDate: Date;
  public maxEventsPerCell = 0;
  protected readonly CalendarType = CalendarType;
  protected contextMenuOpened: boolean;

  @ViewChildren('dayRef')
  protected readonly days: QueryList<CalendarMonthDayCellComponent>;

  protected readonly mwlCalendarMonthView = viewChild.required(CalendarMonthViewComponent);
  protected readonly mwlCalendarMonthViewElement: Signal<ElementRef<HTMLElement>> = viewChild.required(
    CalendarMonthViewComponent,
    {
      read: ElementRef<HTMLElement>,
    },
  );

  private readonly scrollToDateEffect = effect(() => {
    const scrollToDate = this.scrollToDate();
    setTimeout(() => this.scrollToDateHandler(scrollToDate));
  });

  public ngOnChanges(changes: SimpleChanges) {
    if (
      'viewDate' in changes &&
      (changes.viewDate.firstChange ||
        changes.viewDate.previousValue?.getMonth() !== changes.viewDate.currentValue?.getMonth() ||
        changes.viewDate.previousValue?.getFullYear() !== changes.viewDate.currentValue?.getFullYear())
    ) {
      this.displayDate = this.viewDate;
    }

    if ('events' in changes) {
      // Number of weeks in other month may vary, leading to different height of calendar cell
      this.handleResize();
    }
  }

  // SBUX-829 C-1: the grid emits `day.date` as an account-zone-midnight *instant*, but the all-day
  // write path reads it with browser-local getters (Date.UTC(getFullYear…)/beginningOfDay). Emit the
  // DISPLAY (account) zone wall clock so those browser-local reads reconstruct the intended account
  // day instead of the browser's — otherwise clicking/dropping shifts a day when account ≠ browser.
  /** The zone cells are laid out in; the selected-day comparison must use it too. */
  protected get displayTimeZone(): string {
    return this.displayTimeZoneService.timeZone();
  }

  private toDisplayZoneDay(date: Date): Date {
    return toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone());
  }

  protected selectCalendarDate(event: MouseEvent, date: Date, origin: HTMLElement) {
    this.dateClicked.emit({ date: this.toDisplayZoneDay(date), originRef: new ElementRef(origin), event });
  }

  protected dblClickCalendarDate(date: Date) {
    this.dateDblClicked.emit(this.toDisplayZoneDay(date));
  }

  protected handleDrop(dragData: DragData, day: Date, origin: HTMLElement) {
    const localDay = this.toDisplayZoneDay(day);
    this.eventDropped.emit({
      dragData,
      newStart: beginningOfDay(localDay),
      newEnd: endOfDay(localDay),
      originRef: new ElementRef(origin),
    });
  }

  protected handleResize() {
    if (!this.days) {
      return;
    }

    const maxEvents = Math.floor((this.days.first.elementRef.nativeElement.offsetHeight - 50) / 24);
    if (this.maxEventsPerCell !== maxEvents) {
      this.maxEventsPerCell = maxEvents;
    }
  }

  public scrollToDateHandler(date: Date): boolean {
    return AngularCalendarHelper.scrollToDateHandler(
      date,
      this.mwlCalendarMonthView().view.days,
      this.mwlCalendarMonthViewElement().nativeElement,
    );
  }
}
